import { useEffect } from "react";
import { Icons } from "@/components/Icons";

/**
 * Phase F.2 — keyboard shortcuts cheat-sheet.
 *
 * Single source of truth for the staff-app shortcuts. Components
 * binding their own keystrokes should reference this list; if a new
 * combo lands somewhere, add it here so users can discover it.
 *
 * Triggered from Shell on Cmd/Ctrl+/ and from the Help panel.
 */
export const SHORTCUTS: Array<{ group: string; items: Array<{ keys: string[]; label: string }> }> = [
  {
    group: "Navigation",
    items: [
      { keys: ["⌘", "K"],  label: "Open command palette" },
      { keys: ["?"],        label: "Open contextual help drawer" },
      { keys: ["⌘", "/"],  label: "Open this shortcuts overlay" },
      { keys: ["G", "D"],  label: "Go to Dashboard" },
      { keys: ["G", "T"],  label: "Go to Tickets" },
      { keys: ["G", "L"],  label: "Go to Water compliance" },
      { keys: ["G", "V"],  label: "Go to Vehicles" },
      { keys: ["Esc"],      label: "Close any open dialog / overlay" },
    ],
  },
  {
    group: "Lists",
    items: [
      { keys: ["/"],       label: "Focus the search bar" },
      { keys: ["F"],       label: "Open filters" },
      { keys: ["N"],       label: "New record (when applicable)" },
      { keys: ["Shift", "Click"], label: "Multi-select range" },
    ],
  },
  {
    group: "Records",
    items: [
      { keys: ["E"],       label: "Edit current record" },
      { keys: ["A"],       label: "Assign to user" },
      { keys: ["C"],       label: "Add comment" },
      { keys: ["@"],       label: "Mention someone in a comment" },
    ],
  },
];

export default function KeyboardShortcutsOverlay({
  open, onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="scrim" onClick={onClose}
         style={{ position: "fixed", inset: 0, zIndex: 110, background: "rgba(0,0,0,.35)", display: "grid", placeItems: "center" }}>
      <div className="modal" onClick={(e) => e.stopPropagation()}
           style={{ width: 560, maxWidth: "92vw", maxHeight: "85vh", display: "flex", flexDirection: "column" }}>
        <div className="modal-head">
          <div className="kicker">Keyboard shortcuts</div>
          <h2>Get around faster</h2>
        </div>
        <div className="modal-body" style={{ overflowY: "auto" }}>
          {SHORTCUTS.map((g) => (
            <div key={g.group} style={{ marginBottom: 16 }}>
              <div className="kicker" style={{ fontSize: 9 }}>{g.group}</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 8, marginTop: 6 }}>
                {g.items.map((it, i) => (
                  <Row key={i} label={it.label} keys={it.keys} />
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={onClose}>
            <Icons.Check size={14} />Got it
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, keys }: { label: string; keys: string[] }) {
  return (
    <>
      <div style={{ fontSize: 13 }}>{label}</div>
      <div className="row" style={{ gap: 4, justifyContent: "flex-end" }}>
        {keys.map((k, i) => (
          <kbd key={i}
               style={{
                 fontFamily: "var(--font-mono)",
                 fontSize: 11,
                 padding: "2px 6px",
                 borderRadius: 4,
                 border: "1px solid var(--warm-border)",
                 background: "var(--warm-canvas)",
               }}>
            {k}
          </kbd>
        ))}
      </div>
    </>
  );
}
